import { Shimmer, ShimmerElementType, Stack } from "@fluentui/react";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import AIInsightsButtonWithPanel from "../../helper/AIInsightsButtonWithPanel";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../helper/CustomShimmer";
import { useDropdownWithUrlSync } from "../../SharedComponents/Hooks/useDropdownWithUrlSync";
import { useGetSubscriptionByTenantIdQuery } from "../../slices/ExploreCustomers.slice";
import CustomDropdown from "../ProductView/Common/CustomDropdown";
import SubscriptionCharts from "./SubscriptionCharts";
import SubscriptionTable from "./SubscriptionTable";

const SubscriptionContainer = (props: any) => {
  // Properties
  const [filteredItems, setFilteredItems] = React.useState<any[]>([]);
  const [tenantName, setTenantName] = React.useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  // Chart filters as array to handle multiple filters
  const [chartFilters, setChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  const tenantId = searchParams.get("id") ?? undefined;

  // Use the reusable hook for dropdown with URL sync
  const { selectedValue: selectedMetric, handleSelection: handleMetricSelection } = useDropdownWithUrlSync({
    validValues: ["All SKUs", "Security SKUs"],
    defaultValue: "Security SKUs",
    onValueChange: useCallback(() => {
      // Clear chart filters when metric changes
      setChartFilters([]);
      // Clear metric filter URL params to prevent stale filters from being restored
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("mf");
        return newParams;
      });
    }, [setSearchParams])
  });

  const skuType = selectedMetric === "All SKUs" ? "all" : "securitysku";
  const subscriptionData = useGetSubscriptionByTenantIdQuery([tenantId ?? "", skuType]);

  const [dropdownFilteredData, setDropdownFilteredData] = useState<any[]>([]);

  // Handler to manage array of filters
  const handleChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      // Check if filter for this key already exists
      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        // Add new filter
        return [...prevFilters, newFilter];
      } else {
        // Update existing filter
        const updatedFilters = [...prevFilters];
        updatedFilters[existingFilterIndex] = newFilter;
        return updatedFilters;
      }
    });
  };

  const displayMetricDropdownWithButton = subscriptionData.isLoading ? (
    <div style={{ flexBasis: "100%" }}>
      {" "}
      <Shimmer
        shimmerColors={{ shimmer: "var(--shimmer-color)" }}
        styles={{
          root: { paddingTop: 10, paddingBottom: 10, paddingLeft: 10, width: "25%" },
          shimmerWrapper: { ".ms-ShimmerGap-root": { background: "var(--colorBodyBackground1)" } }
        }}
        shimmerElements={[
          { type: ShimmerElementType.line, width: "15%" },
          { type: ShimmerElementType.gap, width: 10 },
          { type: ShimmerElementType.line, width: "15%" }
        ]}
      />{" "}
    </div>
  ) : (
    <FluentProvider theme={webLightTheme}>
      <Stack verticalAlign="start" horizontal horizontalAlign="space-between" styles={{ root: { marginRight: 8 } }}>
        <CustomDropdown
          defaultOption={{
            optionText: selectedMetric,
            optionValue: selectedMetric,
            selectedOptions: [selectedMetric || ""]
          }}
          onSelectedOption={handleMetricSelection}
          data={[
            { value: "Security SKUs", text: "Security SKUs" },
            { value: "All SKUs", text: "All SKUs" }
          ]}
          styles={{ root: { paddingBottom: 10, paddingLeft: 10 } }}
        />
        <AIInsightsButtonWithPanel
          buttonStyle={{ flex: "1 0 auto"}}
          metricId="1"
          panelTitle="License and Subscription Insights"
          tenantId={tenantId}
          tenantName={props.tenantName}
        />
      </Stack>
    </FluentProvider>
  );

  // Events
  const HandleAPIExcpetion = () => {
    const enableAPIException = subscriptionData.error;
    return (enableAPIException && <DisplayAPIsException error={enableAPIException} height={250} />) || "";
  };

  const customProps = {
    tenantId: tenantId,
    tenantName: props?.tenantName
  };

  // Hooks
  useEffect(() => {
    logEvent("License and Subscription", "Customer Page", "SubscriptionContainer", customProps);
  }, []);

  useEffect(() => {
    if (subscriptionData && subscriptionData?.data && subscriptionData?.data?.table) {
      setTenantName(props.tenantName);
      setFilteredItems(subscriptionData?.data?.table);
      logEvent(
        "getSubscriptionByTenant API success and table data loaded",
        "Customer Page - License and Subscription Tab",
        "SubscriptionContainer",
        customProps
      );
    }
    if (subscriptionData.error) {
      logException(
        "Customer Page - License and Subscription Tab",
        "SubscriptionContainer",
        "useGetSubscriptionByTenantIdQuery",
        subscriptionData.error,
        customProps
      );
    }
  }, [subscriptionData.error, subscriptionData, subscriptionData?.data]);

  useEffect(() => {
    const allData = subscriptionData?.data?.table.subscriptionData || [];

    const filteredByMetric = allData.filter(({ isSecurityProduct }: any) => {
      if (selectedMetric === "All SKUs") return true;

      return selectedMetric === "Security SKUs" && (isSecurityProduct === 1 || isSecurityProduct === true);
    });

    setDropdownFilteredData(filteredByMetric);
    setFilteredItems(filteredByMetric);
  }, [subscriptionData.data, selectedMetric]);

  useEffect(() => {
    const filteredData = dropdownFilteredData.filter(({ sku, subscriptionStatus }: any) => {
      // If no filters, show all data
      if (chartFilters.length === 0) {
        return true;
      }

      // Apply all filters - item must match ALL active filters
      return chartFilters.every((filter) => {
        if (filter.filterKey === "sku") {
          return sku === filter.value;
        } else if (filter.filterKey === "subscriptionStatus") {
          return subscriptionStatus === filter.value;
        }
        return true;
      });
    });

    setFilteredItems(filteredData);
  }, [chartFilters, dropdownFilteredData]);

  return (
    <>
      <div>
        {/* Renders Dropdown component */}
        {displayMetricDropdownWithButton}

        <SubscriptionCharts
          key={selectedMetric}
          loading={subscriptionData.isLoading}
          onSelectedChartFilter={handleChartFilter}
          options={{
            filteredData: filteredItems,
            originalData: dropdownFilteredData || [],
            chartOptions: [
              {
                id: 1,
                field: "sku",
                key: "customer-view-license-and-subscription-chart-by-sku",
                title: "SKU Name",
                type: "donut",
                kustoLink: subscriptionData?.data?.table?.skuKustoLink
              },
              {
                id: 2,
                field: "subscriptionStatus",
                key: "customer-view-license-and-subscription-chart-by-subscriptionStatus",
                title: "Subscription Status",
                type: "donut",
                kustoLink: subscriptionData?.data?.table?.subscriptionStatusKustoLink
              }
            ]
          }}
        />
        {subscriptionData.isLoading ? (
          <CustomShimmer columnCount={11} />
        ) : (
          (!subscriptionData.error && (
            <div style={{ marginLeft: 8, marginRight: 5 }}>
              <SubscriptionTable
                tableData={filteredItems}
                tenantName={tenantName}
                kustoLink={subscriptionData?.data?.table?.subscriptionDataKustoLink}
              />
            </div>
          )) ||
          ""
        )}
      </div>
      <HandleAPIExcpetion />
    </>
  );
};

export default SubscriptionContainer;
